import type {BuilderStyle, ComponentStyle, CompRef} from '@wix/document-services-types'
import type {ConversionStyleDataTransformer} from './types'
import type {ExtensionAPI} from '@wix/document-manager-core'
import type {VariantsExtensionAPI} from '../../variants/variants'
import {DATA_TYPES} from '../../../constants/constants'
import {StyleTypes, type ThemeExtAPI} from '../../theme/theme'
import _ from 'lodash'
import type {StructureEntryItem, StyleEntryItem} from '../conversionDal/types'
import type {DeepPartial} from '../types'
import type {BuilderComponentsExtensionAPI} from '../../builderComponents/builderComponents'
import type {StructureExtensionAPI} from '../../structure'

const extractFontSizeNumberFromString = (fontSizeString: string) => (/\d+/.exec(fontSizeString) ?? [0])[0]
const styleDataTransformersByCompType: Record<string, ConversionStyleDataTransformer> = {
    'wysiwyg.viewer.components.WRichText': (style: StyleEntryItem): DeepPartial<ComponentStyle> => {
        return {
            style: {
                properties: {
                    fontSize: style.fontSize
                        ? `${extractFontSizeNumberFromString(style.fontSize[0].data)}spx`
                        : undefined,
                    textAlign: style.alignment?.[0].data
                }
            }
        }
    },
    'wysiwyg.viewer.components.SiteButton': (style: StyleEntryItem): DeepPartial<ComponentStyle> => ({
        style: {
            propertiesOverride: {
                fnt: {
                    fontSize: style.fontSize
                        ? `${extractFontSizeNumberFromString(style.fontSize?.[0].data)}spx`
                        : undefined
                }
            }
        }
    }),
    'wixui.StylableButton': (style: StyleEntryItem): DeepPartial<ComponentStyle> => ({
        style: {
            propertiesOverride: {
                font: {
                    fontSize: style.fontSize
                        ? `${extractFontSizeNumberFromString(style.fontSize?.[0].data)}spx`
                        : undefined
                }
            }
        }
    }),
    'wixui.CollapsibleText': (style: StyleEntryItem): DeepPartial<ComponentStyle> => ({
        style: {
            propertiesOverride: {
                'text-font': {
                    fontSize: style.fontSize
                        ? `${extractFontSizeNumberFromString(style.fontSize?.[0].data)}spx`
                        : undefined
                },
                'button-font': {
                    fontSize: `${style.collapsibleTextButtonFontSize}spx`
                }
            }
        }
    })
}
const retrieveBuilderStyle = (style: StyleEntryItem): DeepPartial<BuilderStyle> | undefined =>
    style.fontSize || style.alignment
        ? [...(style.fontSize ?? []), ...(style.alignment ?? [])].reduce((acc: any, {prop, data, whereToWriteInDM}) => {
              acc[whereToWriteInDM] ??= {}
              acc[whereToWriteInDM][prop] = data
              return acc
          }, {})
        : undefined

export const getStyleTransformer = (
    extensionAPI: ExtensionAPI,
    compPointer: CompRef,
    component: StructureEntryItem,
    styleItem: StyleEntryItem
): CallableFunction | undefined => {
    const compType = (extensionAPI as StructureExtensionAPI).components.getComponentType(compPointer)
    const isBuilder = (extensionAPI as BuilderComponentsExtensionAPI).builderComponents.isBuilder(compType)
    const styleTransformer = isBuilder ? retrieveBuilderStyle : styleDataTransformersByCompType[component.componentType]
    if (!styleTransformer) return
    const {variants} = extensionAPI as VariantsExtensionAPI
    const {theme} = extensionAPI as ThemeExtAPI
    let baseStyleItem = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.theme)

    if (!baseStyleItem || baseStyleItem.type === StyleTypes.topLevelStyle) {
        baseStyleItem = isBuilder
            ? theme.createBuilderStyle(compType)
            : theme.createComponentStyle(component.componentType, component.skin, {}, {}, {})
        delete baseStyleItem.skin
    }

    return () => {
        let mergedObject = _.mergeWith(baseStyleItem, styleTransformer(styleItem))
        mergedObject = _.mergeWith(mergedObject, styleTransformer(styleItem), {
            spx: {
                refWidth: 390,
                resolverType: 'scale'
            }
        })
        if (!isBuilder) mergedObject.style.properties = _.omitBy(mergedObject.style.properties, _.isUndefined)
        return mergedObject
    }
}
